import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useTranslation } from "react-i18next";

export const HowItWorks = () => {
  const navigate = useNavigate();
  const { trackAction } = useAnalytics({ component: "HowItWorks" });
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  
  const screenshots = [
    "/lovable-uploads/f44f44de-c835-452d-b8e4-a0a381602de9.png",
    "/lovable-uploads/ac189c52-e782-4c58-bf02-d991d8c8d9cd.png",
    "/lovable-uploads/d455dadc-395e-4d29-9231-5f61e7e62d4c.png",
    "/lovable-uploads/96482260-6f2e-48f0-8d29-f89b378d82f3.png"
  ];

  const plugin = React.useRef(
    Autoplay({ delay: 5000, stopOnInteraction: false })
  );
  
  const handleGetStarted = () => {
    // Track the click action
    trackAction("how_it_works_get_started_click");
    
    // Get UTM parameters from URL
    const utm_source = searchParams.get("utm_source") || "";
    const utm_medium = searchParams.get("utm_medium") || "";
    const utm_campaign = searchParams.get("utm_campaign") || "";
    
    // Build the navigation URL with UTM parameters
    const authUrl = new URL("/auth", window.location.origin);
    authUrl.searchParams.append("tab", "signup");
    
    // Only add UTM parameters if they exist
    if (utm_source) authUrl.searchParams.append("utm_source", utm_source);
    if (utm_medium) authUrl.searchParams.append("utm_medium", utm_medium);
    if (utm_campaign) authUrl.searchParams.append("utm_campaign", utm_campaign);
    
    navigate(authUrl.pathname + authUrl.search);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-pink-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{t('howItWorks.title')}</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('howItWorks.subtitle')}
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange flex items-center justify-center text-white font-bold">
                1
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t('howItWorks.createProfile')}</h3>
                <p className="text-gray-600">
                  {t('howItWorks.createProfileDescription')}
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange flex items-center justify-center text-white font-bold">
                2
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t('howItWorks.shareLink')}</h3>
                <p className="text-gray-600">
                  {t('howItWorks.shareLinkDescription')}
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange flex items-center justify-center text-white font-bold">
                3
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">{t('howItWorks.getBookings')}</h3>
                <p className="text-gray-600">
                  {t('howItWorks.getBookingsDescription')}
                </p>
              </div>
            </div>

            <Button 
              size="lg"
              className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90 text-white"
              onClick={handleGetStarted}
            >
              {t('howItWorks.getStartedNow')}
            </Button>
          </div>
          
          <div className="relative">
            <div className="relative mx-auto max-w-[300px]">
              <div className="w-full aspect-[9/19] bg-black rounded-[3rem] border-[8px] border-gray-800 shadow-xl overflow-hidden relative">
                {/* Phone notch */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-black rounded-b-3xl z-10"></div>
                
                {/* Screenshots container using Carousel */}
                <div className="absolute inset-0">
                  <Carousel 
                    className="w-full h-full" 
                    opts={{ 
                      align: "start", 
                      loop: true 
                    }}
                    plugins={[plugin.current]}
                  >
                    <CarouselContent>
                      {screenshots.map((src, index) => (
                        <CarouselItem key={src} className="w-full h-full">
                          <img
                            src={src}
                            alt={`App screenshot ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="eager"
                          />
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -z-10 -top-4 -right-4 w-full h-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange rounded-[3rem] opacity-20"></div>
              <div className="absolute -z-10 -bottom-4 -left-4 w-full h-full bg-gradient-to-r from-gebeya-orange to-gebeya-pink rounded-[3rem] opacity-20"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
